import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";
import authService from "../../services/authService";
import stockService from "../../services/stockService";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://delivery-bonfimgourmet-244425130782.southamerica-east1.run.app";

/**
 * StockFormModal Component
 * Modal para criar ou editar entrada de estoque
 */
const StockFormModal = ({ isOpen, onClose, onSave, stock, isSaving }) => {
  const [formData, setFormData] = useState({
    product_id: "",
    storage_location_id: "",
    lot_code: "",
    manufacture_date: "",
    expiration_date: "",
    current_quantity: "",
    reserved_quantity: "0",
    unit_cost: "",
  });

  const [errors, setErrors] = useState({});
  const [products, setProducts] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Carregar dados quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      fetchStorageLocations();
      setErrors({});
    }
  }, [isOpen]);

  // Preencher formulário após produtos carregarem (modo edição)
  useEffect(() => {
    if (isOpen && stock && !loadingProducts && products.length > 0) {
      setFormData({
        product_id: String(stock.product_id || ""),
        storage_location_id: String(stock.storage_location_id || ""),
        lot_code: stock.lot_code || "",
        manufacture_date: stock.manufacture_date
          ? stock.manufacture_date.split("T")[0]
          : "",
        expiration_date: stock.expiration_date
          ? stock.expiration_date.split("T")[0]
          : "",
        current_quantity: String(stock.current_quantity || ""),
        reserved_quantity: String(stock.reserved_quantity || "0"),
        unit_cost: String(stock.unit_cost || ""),
      });
    } else if (isOpen && !stock) {
      // Modo criação - resetar formulário
      setFormData({
        product_id: "",
        storage_location_id: "",
        lot_code: "",
        manufacture_date: "",
        expiration_date: "",
        current_quantity: "",
        reserved_quantity: "0",
        unit_cost: "",
      });
    }
  }, [isOpen, stock, loadingProducts, products]);

  /**
   * Buscar produtos da API
   */
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const token = authService.getToken();
      const response = await axios.get(`${BACKEND_URL}/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const productsData = response.data?.data || response.data || [];
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  /**
   * Buscar locais de armazenamento da API
   */
  const fetchStorageLocations = async () => {
    setLoadingLocations(true);
    try {
      const token = authService.getToken();
      const response = await axios.get(`${BACKEND_URL}/storage-locations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const locationsData = response.data?.data || response.data || [];
      setStorageLocations(Array.isArray(locationsData) ? locationsData : []);
    } catch (error) {
      console.error("Erro ao carregar locais de armazenamento:", error);
      setStorageLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  };

  /**
   * Atualizar campo do formulário
   */
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpar erro do campo quando usuário edita
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * Validar formulário
   */
  const validateForm = () => {
    const newErrors = {};

    // Validar produto apenas no modo de criação
    if (!stock && !formData.product_id) {
      newErrors.product_id = "Produto é obrigatório";
    }

    if (!formData.storage_location_id) {
      newErrors.storage_location_id = "Local de armazenamento é obrigatório";
    }

    if (!formData.lot_code.trim()) {
      newErrors.lot_code = "Código do lote é obrigatório";
    }

    if (!formData.manufacture_date) {
      newErrors.manufacture_date = "Data de fabricação é obrigatória";
    }

    if (!formData.expiration_date) {
      newErrors.expiration_date = "Data de validade é obrigatória";
    }

    if (
      formData.manufacture_date &&
      formData.expiration_date &&
      formData.expiration_date <= formData.manufacture_date
    ) {
      newErrors.expiration_date =
        "Data de validade deve ser posterior à fabricação";
    }

    const currentQty = parseFloat(formData.current_quantity);
    if (
      formData.current_quantity === "" ||
      formData.current_quantity === null ||
      formData.current_quantity === undefined ||
      isNaN(currentQty) ||
      currentQty < 0
    ) {
      newErrors.current_quantity = "Quantidade deve ser maior ou igual a zero";
    }

    const unitCost = parseFloat(formData.unit_cost);
    if (
      formData.unit_cost === "" ||
      formData.unit_cost === null ||
      formData.unit_cost === undefined ||
      isNaN(unitCost) ||
      unitCost <= 0
    ) {
      newErrors.unit_cost = "Custo unitário deve ser maior que zero";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submeter formulário
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Preparar dados para envio
    const stockData = {
      storage_location_id: formData.storage_location_id,
      lot_code: formData.lot_code.trim(),
      manufacture_date: formData.manufacture_date,
      expiration_date: formData.expiration_date,
      current_quantity: parseFloat(formData.current_quantity),
      reserved_quantity: parseFloat(formData.reserved_quantity || 0),
      unit_cost: parseFloat(formData.unit_cost),
    };

    // Adicionar product_id apenas no modo de criação
    if (!stock) {
      stockData.product_id = formData.product_id;
    }

    onSave(stockData);
  };

  /**
   * Obter produto selecionado
   */
  const selectedProduct = products.find(
    (p) => String(p.id) === String(formData.product_id)
  );

  /**
   * Obter local selecionado
   */
  const selectedLocation = storageLocations.find(
    (l) => String(l.id) === String(formData.storage_location_id)
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {stock ? "Editar Entrada de Estoque" : "Nova Entrada de Estoque"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Produto */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="product_id">
                Produto <span className="text-red-500">*</span>
              </Label>
              {stock ? (
                // Modo edição: mostrar produto como read-only
                <div className="space-y-2">
                  <Input
                    value={stock.product_name || "Produto não identificado"}
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="text-sm text-gray-500">
                    Código: {stock.product_code} | Preço:{" "}
                    {stockService.formatCurrency(stock.product_price)}
                  </p>
                  <p className="text-xs text-amber-600">
                    ℹ️ O produto não pode ser alterado após a criação do estoque
                  </p>
                </div>
              ) : (
                // Modo criação: select normal
                <>
                  <Select
                    value={formData.product_id}
                    onValueChange={(value) => handleChange("product_id", value)}
                    disabled={loadingProducts}
                  >
                    <SelectTrigger
                      className={errors.product_id ? "border-red-500" : ""}
                    >
                      <SelectValue
                        placeholder={
                          loadingProducts
                            ? "Carregando produtos..."
                            : "Selecione um produto"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={String(product.id)}>
                          {product.name} -{" "}
                          {stockService.formatCurrency(product.price)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.product_id && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {errors.product_id}
                    </p>
                  )}
                  {selectedProduct && (
                    <p className="text-sm text-gray-500">
                      Código: {selectedProduct.code} | Preço:{" "}
                      {stockService.formatCurrency(selectedProduct.price)}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Local de Armazenamento */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="storage_location_id">
                Local de Armazenamento <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.storage_location_id}
                onValueChange={(value) =>
                  handleChange("storage_location_id", value)
                }
                disabled={loadingLocations}
              >
                <SelectTrigger
                  className={errors.storage_location_id ? "border-red-500" : ""}
                >
                  <SelectValue
                    placeholder={
                      loadingLocations
                        ? "Carregando locais..."
                        : "Selecione um local"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {storageLocations.map((location) => (
                    <SelectItem key={location.id} value={String(location.id)}>
                      {location.name} - {location.equipment_name || "N/A"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.storage_location_id && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.storage_location_id}
                </p>
              )}
              {selectedLocation && (
                <p className="text-sm text-gray-500">
                  Capacidade: {selectedLocation.capacity} | Disponível:{" "}
                  {selectedLocation.capacity -
                    (selectedLocation.used_capacity || 0)}
                </p>
              )}
            </div>

            {/* Código do Lote */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="lot_code">
                Código do Lote <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lot_code"
                placeholder="Ex: L20251216-01"
                value={formData.lot_code}
                onChange={(e) => handleChange("lot_code", e.target.value)}
                className={errors.lot_code ? "border-red-500" : ""}
              />
              {errors.lot_code && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.lot_code}
                </p>
              )}
            </div>

            {/* Data de Fabricação */}
            <div className="space-y-2">
              <Label htmlFor="manufacture_date">
                Data de Fabricação <span className="text-red-500">*</span>
              </Label>
              <Input
                id="manufacture_date"
                type="date"
                value={formData.manufacture_date}
                onChange={(e) =>
                  handleChange("manufacture_date", e.target.value)
                }
                className={errors.manufacture_date ? "border-red-500" : ""}
              />
              {errors.manufacture_date && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.manufacture_date}
                </p>
              )}
            </div>

            {/* Data de Validade */}
            <div className="space-y-2">
              <Label htmlFor="expiration_date">
                Data de Validade <span className="text-red-500">*</span>
              </Label>
              <Input
                id="expiration_date"
                type="date"
                value={formData.expiration_date}
                onChange={(e) =>
                  handleChange("expiration_date", e.target.value)
                }
                className={errors.expiration_date ? "border-red-500" : ""}
              />
              {errors.expiration_date && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.expiration_date}
                </p>
              )}
            </div>

            {/* Quantidade Atual */}
            <div className="space-y-2">
              <Label htmlFor="current_quantity">
                Quantidade Atual <span className="text-red-500">*</span>
              </Label>
              <Input
                id="current_quantity"
                type="number"
                min="0"
                step="1"
                placeholder="0"
                value={formData.current_quantity}
                onChange={(e) =>
                  handleChange("current_quantity", e.target.value)
                }
                className={errors.current_quantity ? "border-red-500" : ""}
              />
              {errors.current_quantity && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.current_quantity}
                </p>
              )}
            </div>

            {/* Quantidade Reservada */}
            <div className="space-y-2">
              <Label htmlFor="reserved_quantity">Quantidade Reservada</Label>
              <Input
                id="reserved_quantity"
                type="number"
                min="0"
                step="1"
                placeholder="0"
                value={formData.reserved_quantity}
                onChange={(e) =>
                  handleChange("reserved_quantity", e.target.value)
                }
              />
              <p className="text-sm text-gray-500">
                Disponível:{" "}
                {(parseFloat(formData.current_quantity) || 0) -
                  (parseFloat(formData.reserved_quantity) || 0)}
              </p>
            </div>

            {/* Custo Unitário */}
            <div className="space-y-2 col-span-2">
              <Label htmlFor="unit_cost">
                Custo Unitário (R$) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="unit_cost"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={formData.unit_cost}
                onChange={(e) => handleChange("unit_cost", e.target.value)}
                className={errors.unit_cost ? "border-red-500" : ""}
              />
              {errors.unit_cost && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {errors.unit_cost}
                </p>
              )}
              {formData.unit_cost && formData.current_quantity && (
                <p className="text-sm text-gray-700 font-medium">
                  Valor Total do Estoque:{" "}
                  {stockService.formatCurrency(
                    parseFloat(formData.unit_cost) *
                      parseFloat(formData.current_quantity)
                  )}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : stock ? (
                "Atualizar"
              ) : (
                "Criar"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default StockFormModal;
